/**
 * What the models believe, as a score (SYR-111 phase 3).
 *
 * The same latent-element DAG the customer survey runs through, so the two
 * sides are one calculation rather than two that agree by luck. Scores are
 * bipolar: 0 is neutral, not the floor, which is the thing everyone reads
 * wrong on first sight.
 */

import React from 'react';
import { Card, Space, Table, Tag, Typography } from 'antd';
import { THEME } from '../../config/theme';
import { getDisplayName } from '../../../../utils/elementDisplayNames';

const { Text, Paragraph } = Typography;

const score = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : value.toFixed(2);

interface ElementRow {
  element: string;
  score: number | null;
}

interface ModelResult {
  label: string;
  retrievalMode: 'on' | 'off';
  result: { overallBrandEquityScore?: number; latentElementScores?: ElementRow[] } | null;
}

interface Props {
  consensus: { result?: any } | null;
  byModel: ModelResult[];
}

// DAG order, so a reader sees the roll-up before its parts rather than an
// alphabetical list that hides the structure. CANONICAL names, because that is
// what the result carries; the rename to marketer-facing names happens at
// render, the way it does everywhere else in the app.
const ORDER = [
  'Brand Equity',
  'Perceived Quality',
  'Brand Loyalty',
  'Willingness to Pay a Premium',
  'Associations',
  'Market Awareness',
  'Alignment Depth',
  'Behavioural Loyalty',
  'Attitudinal Loyalty',
  'Quality Value Tradeoff (PQV)',
  'Brand Attachment',
];

export const OverallScores: React.FunctionComponent<Props> = ({ consensus, byModel }) => {
  const result = consensus?.result;
  if (!result) return null;

  const elements: ElementRow[] = result.latentElementScores ?? [];
  const byName = new Map(elements.map((e) => [e.element, e.score]));
  const overall = result.overallBrandEquityScore ?? null;

  const searched = byModel.filter((m) => m.retrievalMode === 'on');
  const memory = byModel.filter((m) => m.retrievalMode === 'off');
  const memoryFor = (label: string) =>
    memory.find((m) => m.label === label)?.result?.overallBrandEquityScore ?? null;

  return (
    <Card title="What the models believe">
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Space size={40} wrap>
          <Space direction="vertical" size={0}>
            <Text style={{ color: THEME.textSecondary, fontSize: 13 }}>
              AI Brand Equity, panel consensus
            </Text>
            <Text strong style={{ fontSize: 34, lineHeight: 1.1 }}>
              {score(overall)}
            </Text>
            <Text style={{ color: THEME.textSecondary, fontSize: 12 }}>
              on a scale where 0 is neutral, not 50
            </Text>
          </Space>

          {/* Per model, always. Consensus hides the disagreement that matters:
              on the first full read the models sat two positions apart on
              placement while their equity scores spanned five points. */}
          {searched.map((m) => (
            <Space key={m.label} direction="vertical" size={0}>
              <Text style={{ color: THEME.textSecondary, fontSize: 13 }}>{m.label}</Text>
              <Text strong style={{ fontSize: 22, lineHeight: 1.2 }}>
                {score(m.result?.overallBrandEquityScore)}
              </Text>
              <Text style={{ color: THEME.textSecondary, fontSize: 12 }}>
                {memoryFor(m.label) === null
                  ? 'searched'
                  : `${score(memoryFor(m.label))} from memory`}
              </Text>
            </Space>
          ))}
        </Space>

        <Table
          size="small"
          pagination={false}
          rowKey="element"
          dataSource={ORDER.filter((name) => byName.has(name)).map((name) => ({
            element: name,
            score: byName.get(name) ?? null,
          }))}
          columns={[
            {
              title: 'Element',
              dataIndex: 'element',
              render: (name: string) => (
                <Text strong={name === 'Brand Equity'}>{getDisplayName(name)}</Text>
              ),
            },
            {
              title: 'Score',
              dataIndex: 'score',
              width: 120,
              render: (value: number | null) =>
                value === null ? (
                  <Tag>not measured</Tag>
                ) : (
                  <Text strong>{score(value)}</Text>
                ),
            },
          ]}
        />

        <Paragraph style={{ color: THEME.textSecondary, marginBottom: 0, fontSize: 13, maxWidth: '72ch' }}>
          Market Awareness is measured by neither this read nor your survey, so Brand Equity is
          computed over the weights that have data on both sides — deliberately not
          renormalised, because renormalising one side only would make the two numbers
          incomparable.
        </Paragraph>
      </Space>
    </Card>
  );
};
